import { useMemo } from "react";

import { AppMenu, AppMenuSection } from "design-system/AppShell";

import { usePage } from "../../../contexts/PageContext";
import { useAppUI } from "../../../contexts/UIContext";
import { sidebarLinks } from "./links";

/**
 * Side menu — composes the design-system `<AppMenu>` with the
 * project's permission-filtered navigation tree from `./links`.
 *
 * Provenance — `<AppMenu>` is skill-derived; the wiring + section
 * tree are project-specific.
 */
function Sidebar() {
  const { navigate, page } = usePage();
  const { user } = useAppUI();
  const permissions = user?.permissions || {};

  const hasPermission = (userRight: string | undefined): boolean => {
    if (!userRight) return true;
    return Object.keys(permissions).includes(userRight);
  };

  const handleLinkClick = (action: string, url: string = ""): void => {
    if (url !== "") {
      window.open(url, "_blank")?.focus();
      return;
    }
    navigate(action);
  };

  const sections = useMemo<AppMenuSection[]>(
    () =>
      sidebarLinks
        .filter((item) => hasPermission(item.userRight))
        .map((item) => ({
          header: item.header,
          icon: item.icon,
          links: item.links
            .filter((link) => hasPermission(link.userRight))
            .map((link) => ({
              id: `${item.header}::${link.name}`,
              label: link.name.replace(/^-/, ""),
              active:
                !!link.action &&
                !link.url &&
                link.action === page,
              onClick: link.url
                ? () => handleLinkClick(link.action, link.url)
                : () => handleLinkClick(link.action),
            })),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, page]
  );

  return <AppMenu sections={sections} />;
}

export default Sidebar;
